import { Person } from '../graph/types';

export interface SavedGroup {
  id: string;
  name: string;
  people: Person[];
}

const STORAGE_KEY = 'find-time-saved-groups';
const SETTINGS_KEY = 'find-time-settings';

/**
 * Check if Office Roaming Settings is available
 */
function isRoamingAvailable(): boolean {
  return !!(
    typeof Office !== 'undefined' &&
    Office.context &&
    Office.context.roamingSettings
  );
}

/**
 * Get all saved groups from Office Roaming Settings (syncs across clients)
 * Falls back to localStorage if roaming settings unavailable
 */
export function getSavedGroups(): SavedGroup[] {
  try {
    if (isRoamingAvailable()) {
      const stored = Office.context.roamingSettings.get(STORAGE_KEY);
      if (!stored) return [];
      // Roaming settings stores objects directly, no need to parse
      return Array.isArray(stored) ? stored : [];
    } else {
      // Fallback to localStorage
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading saved groups:', error);
    return [];
  }
}

/**
 * Save a new group (async to support roaming settings)
 */
export async function saveGroup(name: string, people: Person[]): Promise<SavedGroup> {
  const groups = getSavedGroups();

  const newGroup: SavedGroup = {
    id: generateId(),
    name,
    people,
  };

  groups.push(newGroup);

  await saveGroupsToStorage(groups);
  return newGroup;
}

/**
 * Delete a saved group (async to support roaming settings)
 */
export async function deleteGroup(id: string): Promise<void> {
  const groups = getSavedGroups();
  const filtered = groups.filter(g => g.id !== id);
  await saveGroupsToStorage(filtered);
}

/**
 * Update an existing group (async to support roaming settings)
 */
export async function updateGroup(id: string, name: string, people: Person[]): Promise<void> {
  const groups = getSavedGroups();
  const index = groups.findIndex(g => g.id === id);

  if (index === -1) {
    throw new Error('Group not found');
  }

  groups[index] = { id, name, people };
  await saveGroupsToStorage(groups);
}

/**
 * Internal function to save groups to storage
 */
async function saveGroupsToStorage(groups: SavedGroup[]): Promise<void> {
  if (isRoamingAvailable()) {
    Office.context.roamingSettings.set(STORAGE_KEY, groups);

    return new Promise((resolve, reject) => {
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log('Groups saved to roaming settings');
          resolve();
        } else {
          console.error('Failed to save to roaming settings:', result.error);
          reject(new Error('Failed to save group'));
        }
      });
    });
  } else {
    // Fallback to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
    } catch (error) {
      console.error('Error saving group:', error);
      throw new Error('Failed to save group');
    }
  }
}

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * App settings interface
 */
export interface AppSettings {
  includeAttendeesInEmail: boolean;
  workingHoursStart: string; // "HH:mm" format
  workingHoursEnd: string;   // "HH:mm" format
}

/**
 * Get app settings from Office Roaming Settings (syncs across clients)
 * Falls back to localStorage if roaming settings unavailable
 */
export function getSettings(): AppSettings {
  const defaults: AppSettings = {
    includeAttendeesInEmail: true,
    workingHoursStart: '09:00',
    workingHoursEnd: '17:00',
  };

  try {
    if (isRoamingAvailable()) {
      const stored = Office.context.roamingSettings.get(SETTINGS_KEY);
      if (!stored) return defaults;
      return { ...defaults, ...stored };
    } else {
      const stored = localStorage.getItem(SETTINGS_KEY);
      if (!stored) return defaults;
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading settings:', error);
    return defaults;
  }
}

/**
 * Save app settings (async to support roaming settings)
 */
export async function saveSettings(settings: AppSettings): Promise<void> {
  if (isRoamingAvailable()) {
    Office.context.roamingSettings.set(SETTINGS_KEY, settings);

    return new Promise((resolve, reject) => {
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log('Settings saved to roaming settings');
          resolve();
        } else {
          console.error('Failed to save settings to roaming:', result.error);
          reject(new Error('Failed to save settings'));
        }
      });
    });
  } else {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving settings:', error);
      throw new Error('Failed to save settings');
    }
  }
}
